'use client';

import Image from 'next/image';
import { useState } from 'react';
import { useGetClientQuery } from '@/app/(main)/client-detail/hooks/useGetClientQuery';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { ViewFileByType } from '@/app/components/ViewFileByType';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import PdfPreview from '@/app/components/cards/FileCard/PdfPreview';
import type { ClientTaskData, TaskAttachment, TaskStatus } from '@/types/clientTasksApiTypes';
import { fileTypes } from '@/types/common';
import { cn } from '@/utils/сn';
import { TASK_REMINDER_VIEW_LABELS, TASK_STATUS_BG, TASK_STATUS_LABEL } from '../../../../../consts/client-detail';
import { getReminderDaysFromTask } from '../../utils/getReminderDaysFromTask';
import { getTaskPersonName } from '../../utils/getTaskPersonName';
import ClockIcon from '/public/icons/clock.svg';

const formatDeadline = (deadline: string) => {
  const dateStr = deadline?.slice(0, 10);
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

interface ViewTaskModalProps {
  task: ClientTaskData | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (task: ClientTaskData) => void;
}

const STATUS_CONFIG: Record<TaskStatus, { bg: string; label: string }> = {
  OPEN: { bg: TASK_STATUS_BG.OPEN, label: TASK_STATUS_LABEL.OPEN },
  COMPLETED: { bg: TASK_STATUS_BG.COMPLETED, label: TASK_STATUS_LABEL.COMPLETED },
  CLOSED: { bg: TASK_STATUS_BG.CLOSED, label: TASK_STATUS_LABEL.CLOSED },
};

interface AvatarProps {
  name: string;
  imageRelativeUrl?: string | null;
  useFallbackLogo?: boolean;
}

const UserAvatar = ({ name, imageRelativeUrl, useFallbackLogo = false }: AvatarProps) => {
  const [imgError, setImgError] = useState(false);

  const initials =
    (name ?? '')
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';

  const resolvedSrc = imageRelativeUrl
    ? imgError
      ? '/images/smartflyer-profile-fallback.jpeg'
      : imageRelativeUrl
    : useFallbackLogo
      ? '/images/smartflyer-profile-fallback.jpeg'
      : null;

  if (resolvedSrc) {
    return (
      <div
        className="w-8 h-8 rounded-full overflow-hidden shrink-0"
        style={{ border: '0.5px solid rgba(33,37,41,0.08)' }}
      >
        <Image
          src={resolvedSrc}
          alt={name}
          width={32}
          height={32}
          className="object-cover w-full h-full"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
      <span className="text-blue-900 text-[11px] font-medium font-inter">{initials}</span>
    </div>
  );
};

interface ViewAttachmentModalProps {
  attachment: TaskAttachment | null;
  onClose: () => void;
}

const ViewAttachmentModal = ({ attachment, onClose }: ViewAttachmentModalProps) => {
  if (!attachment) return null;
  const url = attachment.media?.remote_url ?? '';
  const name = attachment.media?.raw_filename ?? '';
  const isPdf = attachment.media?.file_mime_type === 'application/pdf' || url.toLowerCase().endsWith('.pdf');
  const type = isPdf ? fileTypes.PDF : fileTypes.IMAGE;
  return (
    <Modal open={!!attachment} onClose={onClose} variant="fullScreenMobile">
      <div className="flex flex-col items-center gap-6 overflow-hidden max-md:h-full max-md:w-full max-md:px-4 max-md:py-8 md:py-[46px] md:px-9 md:w-[550px]">
        <div className="flex flex-col gap-[6px] text-center">
          <h2 className="font-canela tracking-[0.6px] text-blue-900">{name}</h2>
        </div>
        <ViewFileByType
          type={type}
          path={url}
          caption={name}
          name={name}
          contentDimensions={{ width: 472, height: 420 }}
        />
        <BaseButton
          intent="secondary"
          className="w-full !px-[22px] !py-[9px] !text-base !font-medium !leading-6 !h-auto !min-h-0 !rounded-[6px]"
          onClick={onClose}
        >
          Close
        </BaseButton>
      </div>
    </Modal>
  );
};

interface FilePreviewProps {
  attachment: TaskAttachment;
  onOpen: (attachment: TaskAttachment) => void;
}

const FilePreview = ({ attachment, onOpen }: FilePreviewProps) => {
  const [imgError, setImgError] = useState(false);
  const url = attachment.media?.remote_url ?? (attachment as { url?: string }).url ?? '';
  const name = attachment.media?.raw_filename ?? (attachment as { name?: string }).name ?? '';
  const isPdf = attachment.media?.file_mime_type === 'application/pdf' || url.toLowerCase().endsWith('.pdf');

  return (
    <button
      type="button"
      onClick={() => onOpen(attachment)}
      className="w-[82px] h-[82px] rounded-[6px] overflow-hidden bg-grey-100 shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
    >
      {isPdf ? (
        <PdfPreview fileUrl={url} containerWidth={82} containerHeight={82} className="rounded-[6px]" />
      ) : !imgError ? (
        <Image
          src={url}
          alt={name}
          width={82}
          height={82}
          className="object-cover w-full h-full"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-grey-100">
          <span className="text-grey-400 text-[10px] text-center px-1 font-inter">{name}</span>
        </div>
      )}
    </button>
  );
};

export const ViewTaskModal = ({ task, isOpen, onClose, onEdit }: ViewTaskModalProps) => {
  const [selectedAttachment, setSelectedAttachment] = useState<TaskAttachment | null>(null);

  const { data: clientQueryResponse } = useGetClientQuery(undefined, {
    clientIdOverride: task ? String(task.client_id) : null,
  });

  if (!task) return null;

  const statusConfig = STATUS_CONFIG[task.status] ?? STATUS_CONFIG.OPEN;
  const attachments = task.attachments ?? task.files ?? [];
  const hasFiles = attachments.length > 0;

  const clientName = getTaskPersonName(task.client) || null;
  const agentName = getTaskPersonName(task.agent);

  const clientImageUrl = task.client?.image_relative_url ?? clientQueryResponse?.data?.profile_picture_url ?? null;
  const agentImageUrl = task.agent?.image_relative_url ?? null;

  const reminderDays = getReminderDaysFromTask(task);
  const reminderLabel = reminderDays != null ? TASK_REMINDER_VIEW_LABELS[reminderDays] : null;

  return (
    <>
      <Modal open={isOpen} onClose={onClose} variant="fullScreenMobile">
        <div className="flex flex-col overflow-hidden max-md:h-full max-md:w-full md:w-[550px] md:max-h-[90vh] md:py-[46px]">
          <div className="flex flex-col gap-[6px] px-4 pt-8 md:px-9 md:pt-0 text-center shrink-0 mb-[30px] max-md:mb-4">
            <ModalMobileHeader title="Task details" onBack={onClose} />
            <p className="font-inter font-normal text-[16px] leading-6 text-grey-700 max-md:hidden">
              View task information and associated details.
            </p>
          </div>

          <div className="flex flex-col gap-5 px-4 md:px-9 overflow-y-auto flex-1 min-h-0">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-[6px]">
                <span className="font-inter font-medium md:text-[20px] text-base leading-6 text-black">
                  {task.title}
                </span>
                {(task.description || task.details) && (
                  <p className="font-inter font-normal md:text-base text-sm leading-6 text-black">
                    {task.description ?? task.details}
                  </p>
                )}
              </div>
            </div>

            <div className="h-px bg-grey-300" />
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-[6px]">
                  <span className="font-inter font-semibold md:text-base text-sm leading-6 text-black">Status</span>
                  <div
                    className="inline-flex items-center px-2 py-[2px] rounded-[4px] self-start h-[22px]"
                    style={{ background: statusConfig.bg }}
                  >
                    <span className="font-inter font-normal text-[14px] leading-[19.6px] text-blue-900">
                      {statusConfig.label}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-[6px]">
                  <span className="font-inter font-semibold md:text-base text-sm leading-6 text-black">Deadline</span>
                  <span className="font-inter font-normal md:text-base text-sm leading-6 text-black">
                    {formatDeadline(task.deadline)}
                  </span>
                </div>
              </div>
              {reminderLabel && (
                <div className="flex items-start gap-2 self-stretch">
                  <ClockIcon className="size-6 shrink-0 text-black" />
                  <span className="flex-1 font-inter font-normal md:text-base text-sm leading-6 text-black">
                    {reminderLabel}
                  </span>
                </div>
              )}
            </div>

            <div className="h-px bg-grey-300" />
            <div className="grid grid-cols-2 gap-4">
              {clientName && (
                <div className={cn('flex flex-col gap-[6px] min-w-0', !task.agent && 'col-span-2')}>
                  <span className="font-inter font-semibold md:text-base text-sm leading-6 text-black">
                    Associated Client
                  </span>
                  <div className="flex items-center gap-2 min-w-0">
                    <UserAvatar name={clientName} imageRelativeUrl={clientImageUrl} />
                    <span className="flex-1 font-inter font-normal md:text-base text-sm leading-6 line-clamp-2 break-all min-w-0 text-blue-900">
                      {clientName}
                    </span>
                  </div>
                </div>
              )}
              {task.agent && (
                <div className="flex flex-col gap-[6px]">
                  <span className="font-inter font-semibold md:text-base text-sm leading-6 text-black">
                    Assigned Agent
                  </span>
                  <div className="flex items-center gap-2">
                    <UserAvatar name={agentName} imageRelativeUrl={agentImageUrl} useFallbackLogo />
                    <span className="font-inter font-normal md:text-base text-sm leading-6 text-blue-900">
                      {agentName}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {hasFiles && (
              <>
                <div className="h-px bg-grey-300" />
                <div className="flex flex-col gap-2">
                  <span className="font-inter font-semibold md:text-base text-sm leading-6 text-black">
                    Task File(s)
                  </span>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, 82px)',
                      gap: '12px',
                      justifyContent: 'space-between',
                    }}
                  >
                    {attachments.map((file) => (
                      <FilePreview
                        key={file.uuid ?? (file as { id?: number }).id}
                        attachment={file}
                        onOpen={setSelectedAttachment}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-5 px-4 md:px-9 mt-[30px] max-md:mt-0 pt-6 pb-8 md:pb-0 shrink-0">
            <BaseButton
              intent="secondary"
              className="flex-1 !px-[22px] !py-[9px] !text-base !font-medium !leading-6 !h-auto !min-h-0 !rounded-[6px]"
              onClick={onClose}
            >
              Cancel
            </BaseButton>
            <BaseButton
              intent="primary"
              className="flex-1 !px-[22px] !py-[9px] !text-base !font-medium !leading-6 !h-auto !min-h-0 !rounded-[6px]"
              onClick={() => onEdit(task)}
            >
              Edit Task
            </BaseButton>
          </div>
        </div>
      </Modal>
      <ViewAttachmentModal attachment={selectedAttachment} onClose={() => setSelectedAttachment(null)} />
    </>
  );
};
